import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResult, AxiosResultDefaultError } from '../../../api/request';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/Info/DisplayError';
import InputLoading from '../../../components/Info/InputLoading';

import { getClassroomAvailability } from '../api';

export interface ClassroomLoaderFilters {
  lessonId?: number;
  startModuleId?: number;
  endModuleId?: number;
  date?: string;
}

interface BaseLoaderProps {
  filters: ClassroomLoaderFilters;
  children: ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  ClassroomLoaderFilters,
  any[] | undefined,
  AxiosResultDefaultError
>();

export const useClassroomLoader = useFetch;

export const formDataLoaderConsumer = FetchConsumer;

export default function ClassroomLoader({
  children,
  filters,
}: BaseLoaderProps) {
  const { t } = useTranslation();

  const request = useCallback(async () => {
    if (Object.values(filters).some((value) => value === undefined)) {
      return Promise.resolve<AxiosResult<any[], AxiosResultDefaultError>>(
        [] as any,
      );
    }
    return getClassroomAvailability(filters);
  }, [filters]);

  return (
    <FetchProvider
      request={request}
      defaultQuery={filters}
      fetchImmediately={true}
    >
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <InputLoading inputLabel={t(`classesHistory.classroom`)} />;
          } else if (error) {
            if (error.data.message === 'CAMPUS_NOT_FOUND') {
              return (
                <DisplayError
                  textBody={t(`classesHistory.errors.classroomWithoutCampus`)}
                  retryAction={refresh}
                />
              );
            }
            return <DisplayError textBody={error.code} retryAction={refresh} />;
          } else if (data) {
            return children;
          } else {
            return null;
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
